/**
 * @file Lead upload files page.
 * @description Lists uploaded lead CSV files and supports per-file download by id.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Download, FileText, History, RefreshCw, Upload } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { LoadingSpinner, PageHeader, Pagination, Panel } from '../components/ui';
import { PAGINATION } from '../constants/api';
import type { LeadUploadFile } from '../types/api';
import { LeadUploadFilesService } from '../services/leadUploadFilesService';

const PER_PAGE = PAGINATION.DEFAULT_PER_PAGE;
const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
const TD = 'px-4 py-4 text-sm text-gray-900 align-top';

const LeadUploadFiles: React.FC = () => {
  const [files, setFiles] = useState<LeadUploadFile[]>([]);
  const [allFiles, setAllFiles] = useState<LeadUploadFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PER_PAGE);
  const [serverPaginated, setServerPaginated] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const initialFetchDoneRef = useRef(false);

  const applyClientPage = useCallback((rows: LeadUploadFile[], page: number) => {
    const total = rows.length;
    const pages = Math.max(1, Math.ceil(total / PER_PAGE));
    const safePage = Math.min(Math.max(1, page), pages);
    const start = (safePage - 1) * PER_PAGE;

    setAllFiles(rows);
    setFiles(rows.slice(start, start + PER_PAGE));
    setCurrentPage(safePage);
    setTotalPages(pages);
    setTotalRecords(total);
    setPageSize(PER_PAGE);
  }, []);

  const fetchFiles = useCallback(async (page: number = 1) => {
    const isFirstPaint = !initialFetchDoneRef.current;
    setErrorMsg(null);

    if (isFirstPaint) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const result = await LeadUploadFilesService.listFiles(page, PER_PAGE);
      const hasServerPaging =
        result.pagination.total > result.files.length ||
        result.pagination.total_page > 1 ||
        result.pagination.page === page;

      setServerPaginated(hasServerPaging);

      if (hasServerPaging) {
        setFiles(result.files);
        setAllFiles(result.files);
        setCurrentPage(result.pagination.page);
        setTotalPages(result.pagination.total_page);
        setTotalRecords(result.pagination.total);
        setPageSize(result.pagination.per_page || PER_PAGE);
      } else {
        applyClientPage(result.files, page);
      }
    } catch (error) {
      setErrorMsg(error instanceof Error ? error.message : 'Failed to load upload files');
      setFiles([]);
      setAllFiles([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setRefreshing(false);
      initialFetchDoneRef.current = true;
    }
  }, [applyClientPage]);

  useEffect(() => {
    void fetchFiles(1);
  }, [fetchFiles]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages || page === currentPage || refreshing) {
        return;
      }

      if (serverPaginated) {
        void fetchFiles(page);
        return;
      }

      applyClientPage(allFiles, page);
    },
    [
      allFiles,
      applyClientPage,
      currentPage,
      fetchFiles,
      refreshing,
      serverPaginated,
      totalPages,
    ],
  );

  const canDownloadFile = useCallback((status: string) => {
    return status.trim().toLowerCase() !== 'failed';
  }, []);

  const handleDownload = useCallback(async (file: LeadUploadFile) => {
    if (!canDownloadFile(file.status)) return;
    setDownloadingId(file.id);
    try {
      const { blob, filename } = await LeadUploadFilesService.downloadFile(
        file,
        file.filename || `lead-upload-${file.id}.csv`,
      );
      LeadUploadFilesService.triggerDownload(blob, filename);
    } catch (error) {
      setErrorMsg(error instanceof Error ? error.message : 'Failed to download file');
    } finally {
      setDownloadingId(null);
    }
  }, [canDownloadFile]);

  const formatUploadDate = useCallback((dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const normalized = dateString.includes('T')
        ? dateString
        : dateString.replace(' ', 'T');
      return format(parseISO(normalized), 'MMM dd, yyyy HH:mm');
    } catch {
      return LeadUploadFilesService.formatDate(dateString);
    }
  }, []);

  const displayValue = useCallback((value: string) => {
    const text = value.trim();
    return text || 'N/A';
  }, []);

  const serialOffset = useMemo(
    () => (currentPage - 1) * pageSize,
    [currentPage, pageSize],
  );

  const isFetchError = !loading && Boolean(errorMsg) && files.length === 0;
  const isEmpty = !loading && !errorMsg && files.length === 0;
  const hasData = !loading && files.length > 0;

  const handleRetry = () => {
    initialFetchDoneRef.current = false;
    setErrorMsg(null);
    void fetchFiles(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lead Upload Files"
        subtitle="View and download previously uploaded lead files"
        icon={History}
        className="shadow-md"
      />

      <Panel padded className="min-h-[300px] p-0">
        <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-900">All Upload Files</h3>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <LoadingSpinner size="lg" label="Loading upload files" />
            <p className="mt-4 text-sm font-medium text-gray-600">Loading upload files...</p>
          </div>
        ) : isFetchError ? (
          <div className="flex flex-col items-center justify-center px-4 py-14 sm:px-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
              <AlertCircle size={32} className="text-red-500" aria-hidden />
            </div>
            <h4 className="mt-5 text-lg font-semibold text-gray-900">Failed to load upload files</h4>
            <p className="mt-2 max-w-md text-center text-sm text-gray-600">{errorMsg}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center px-4 py-14 sm:px-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <FileText size={32} className="text-gray-400" aria-hidden />
            </div>
            <h4 className="mt-5 text-lg font-semibold text-gray-900">No upload files found</h4>
            <p className="mt-2 max-w-md text-center text-sm text-gray-600">
              There are no uploaded lead files to display yet. Upload a CSV file to see it listed here.
            </p>
            <Link
              to="/leads/upload"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-primary-500 to-secondary-500 px-4 py-2.5 text-sm font-medium text-white transition-all hover:from-primary-600 hover:to-secondary-600"
            >
              <Upload size={16} />
              Go to Upload Leads
            </Link>
          </div>
        ) : hasData ? (
          <>
            {errorMsg && (
              <div className="mx-4 mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:mx-6">
                <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-500" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">Something went wrong</p>
                  <p className="mt-0.5">{errorMsg}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setErrorMsg(null)}
                  className="shrink-0 text-xs font-medium text-red-700 underline hover:text-red-800"
                >
                  Dismiss
                </button>
              </div>
            )}

            <div className="relative">
              {refreshing && (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-[1px]"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-md border border-gray-100">
                    <LoadingSpinner size="sm" centered={false} label="Updating upload files" />
                    <span className="text-sm font-medium text-gray-700">Updating…</span>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={`${TH} text-center w-16`}>S.No</th>
                      <th className={TH}>File Name</th>
                      <th className={TH}>Advertiser</th>
                      <th className={TH}>Campaign</th>
                      <th className={TH}>Status</th>
                      <th className={TH}>Uploaded At</th>
                      <th className={`${TH} text-right`}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {files.map((file, index) => {
                      const downloadable = canDownloadFile(file.status);
                      return (
                      <tr key={file.id} className="hover:bg-gray-50 transition-colors">
                        <td className={`${TD} text-center tabular-nums text-gray-600`}>
                          {serialOffset + index + 1}
                        </td>
                        <td className={TD}>
                          <div className="font-medium text-gray-900 break-words">
                            {displayValue(file.filename)}
                          </div>
                        </td>
                        <td className={TD}>{displayValue(file.advertiser_name)}</td>
                        <td className={TD}>{displayValue(file.campaign_name)}</td>
                        <td className={TD}>{displayValue(file.status)}</td>
                        <td className={`${TD} whitespace-nowrap`}>
                          {formatUploadDate(file.uploaded_at)}
                        </td>
                        <td className={`${TD} text-right whitespace-nowrap`}>
                          <button
                            type="button"
                            onClick={() => void handleDownload(file)}
                            disabled={!downloadable || downloadingId === file.id}
                            className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-primary-500 to-secondary-500 px-3 py-2 text-xs font-medium text-white shadow-sm transition-all hover:from-primary-500 hover:to-secondary-500 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Download size={14} />
                            {downloadingId === file.id ? 'Downloading…' : 'Download'}
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <Pagination
              className="mt-0"
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              itemLabel="files"
              disabled={refreshing}
              ariaLabel="Upload files pagination"
            />
          </>
        ) : null}
      </Panel>
    </div>
  );
};

export default LeadUploadFiles;
